
import { Mixins, Component, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";

import EventRecordsMixin from "./EventRecordsMixin";
import EventRecordsCCure from "./EventRecordsCCure.vue";

import { EventRecord } from "@/store/site-monitor/types";
import { MetadataRequest, MetadataLookup, Metadata, NewMetadataItem } from "@/store/metadata/types";
import { DynamicLinkConfig } from "@/store/types";

import { stringMixin } from "@/mixins";
const { formatString } = stringMixin.methods;

const SiteMonitorStore = namespace("siteMonitor");
const MetadataStore = namespace("metadata");

interface ActiveResponses {
	[responseId: number]: any;
}

interface TaskModalState {
	metadata: Metadata[] | null;
	responseId: number | null;
	responseTitle: string;
	visible: boolean;
}

@Component({
	components: {
		"event-records-ccure": EventRecordsCCure,
	},
})
export default class EventRecordsCCureTasks extends Mixins(EventRecordsMixin) {
	@Getter private getFeature: (featureNames: string[]) => boolean;
	@Getter("getDynamicLinkConfig") private dynamicLinkConfig: DynamicLinkConfig;

	@SiteMonitorStore.State("ActiveResponseIDs") private activeResponses: ActiveResponses;
	@SiteMonitorStore.Action private createAuditRecord: ({
		eventId,
		eventRecordTypeId,
		details,
		objectId,
	}) => Promise<number>;

	@MetadataStore.Action private loadMetadataTypes: () => Promise<void>;
	@MetadataStore.Action private loadMetadata: (metadataRequest: MetadataRequest) => Promise<void>;
	@MetadataStore.Action private createMetadata: (newMetadata: NewMetadataItem) => Promise<void>;
	@MetadataStore.Action private deleteMetadata: (metadataToDelete: Metadata) => Promise<void>;
	@MetadataStore.Getter private getMetadataForObject: (lookup: MetadataLookup) => Metadata | Metadata[];

	private metadataRefreshTimeout: any = null;
	private newTaskIdString: string = "";
	private taskModalState: TaskModalState = {
		metadata: null,
		responseId: null,
		responseTitle: "",
		visible: false,
	};

	/**
	 * Getter to retrieve just the Ids of our active
	 */
	private get activeResponseIds() {
		return Object.keys(this.activeResponses);
	}

	/**
	 * Returns an object with key/value pairs for each response id/metadata.
	 */
	private get responsesMetadata(): { [responseId: string]: Metadata[] } {
		// Build our key/value object by reducing our array of response id's
		return this.activeResponseIds.reduce((obj, id) => {
			let metadata = this.getMetadataForObject({
				objectId: parseInt(id),
				objectType: "Responses",
				metadataType: "ThirdPartyTaskSystem",
			});

			if (metadata) {
				obj[id] = Array.isArray(metadata) ? metadata : [metadata];
			}

			return obj;
		}, {});
	}

	/**
	 * Returns the formatted new task link. If the user has just entered a number, or a task number,
	 * such as 12345 or T12345, we return the full formatted task URL. If the user has entered the full URL,
	 * we validate it and return it. Otherwise we return an empty string.
	 */
	private get formattedNewTaskUrl() {
		return this.getTaskUrl(this.newTaskIdString);
	}

	/**
	 * Returns true if the feature flags necessary for the third party tasks integration are enabled, and
	 * link config is setup.
	 */
	private get tasksEnabled() {
		return (
			this.getFeature(["Integration", "Tasks"]) &&
			this.getFeature(["Integration", "DynamicLink"]) &&
			this.dynamicLinkConfig != null
		);
	}

	/**
	 * Loads metadata info from the api, starts up our metadata refresh timeout.
	 */
	private async created() {
		// Load metadata types
		await this.loadMetadataTypes();

		const refreshMetadata = async () => {
			await this.updateMetadata();
			this.metadataRefreshTimeout = setTimeout(refreshMetadata, 5000);
		};

		await refreshMetadata();
	}

	/**
	 * Ensures the metadata refresh timeout is cleaned up before the component is destroyed.
	 */
	private destroyed() {
		clearTimeout(this.metadataRefreshTimeout);
	}

	/**
	 * Watches for when our set of response Id's changes. When it does, we'll refresh our metadata
	 */
	@Watch("activeResponseIds", { immediate: true })
	private async onActiveResponsesChanged(activeResponses: ActiveResponses) {
		await this.updateMetadata();
	}

	/**
	 * Triggers an update of metadata for the currently active responses.
	 */
	private async updateMetadata() {
		const responseIds = Object.keys(this.activeResponses);

		await this.loadMetadata({
			metadataType: "ThirdPartyTaskSystem",
			objects: responseIds.map((id) => ({
				objectId: parseInt(id),
				objectType: "Responses",
			})),
		});
	}

	/**
	 * Returns true if we have an item of metadata for a given response.
	 */
	private hasMetadataForResponse(responseId: number) {
		if (
			responseId == null ||
			this.responsesMetadata[responseId] == null ||
			this.responsesMetadata[responseId].length == 0
		) {
			return false;
		}

		return true;
	}

	/**
	 * Returns true if the given event record has a type which supports linking to
	 * the responses table (and therefore can have Tasks metadata associated with it).
	 */
	private canRecordHaveMetadata(eventRecord: EventRecord) {
		if (eventRecord == null || !eventRecord.objectID) {
			return false;
		}

		// EventRecordTypes that support linking to Respo nses
		const responseLinkedRecordTypes = [1 /*, 27, 40*/];
		return responseLinkedRecordTypes.includes(eventRecord.eventRecordTypeID);
	}

	/**
	 * Returns the title to use for the task icon on an event record row.
	 */
	private getTaskIconTitle(responseId: number) {
		const metadataForResponse = this.responsesMetadata[responseId];

		if (metadataForResponse == null) {
			return "No task assigned to this alarm, click to add one";
		} else {
			return `Tasks are assigned to this alarm: ${this.getMetadataTaskTitles(metadataForResponse)}`;
		}
	}

	/**
	 * Returns the URL for a given task ID, using the regex and format string provided
	 * via the Dynamic Link config.
	 */
	private getTaskUrl(taskId: string) {
		if (!this.dynamicLinkConfig || !this.dynamicLinkConfig.linkFormatter) {
			return null;
		}

		let taskIdMatch = taskId.match(this.dynamicLinkConfig.testPattern);
		if (!taskIdMatch) {
			return null;
		}

		return formatString(this.dynamicLinkConfig.linkFormatter, ...taskIdMatch);
	}

	/**
	 * Formats the task ids for a set of metadata into a comma seperated list.
	 */
	private getMetadataTaskTitles(metadata: Metadata[]) {
		if (!metadata) {
			return "";
		}

		const taskIds: string[] = metadata.map((taskItem) => taskItem.data.taskId);
		return taskIds.reduce((prev, curr) => {
			return (prev == "" ? "" : prev + ", ") + curr;
		}, "");
	}

	/** Task Modal Methods */

	/**
	 * Opens the task modal for a specific response. If the response already has
	 * an assigned metadata item, then we attach it to our modal state so we can
	 * display it. Otherwise, the user will be prompted to enter a new task id/url.
	 */
	private openTaskModal(responseId: number, responseTitle: string) {
		const metadata = this.responsesMetadata[responseId] || null;

		this.newTaskIdString = "";
		this.taskModalState = {
			responseId,
			responseTitle,
			visible: true,
			metadata,
		};
	}

	/**
	 * Closes the task modal.
	 */
	private closeTaskModal() {
		this.taskModalState.visible = false;
	}

	/**
	 * Creates a new task metadata item linked to the response currently
	 * open in the task modal. Extracts the task id (number) from the newTaskIdString,
	 * creates a new metadata item, and sends it to the api.
	 */
	private async addNewTaskLink() {
		const objectId = this.taskModalState.responseId;
		const taskId = this.newTaskIdString;

		// Create the metadata item and save it to the DB
		await this.createMetadata({
			metadataType: "ThirdPartyTaskSystem",
			objectId,
			objectType: "Responses",
			data: {
				taskId: taskId,
			},
		});

		// Create an audit record
		const responseDetails = this.activeResponses[objectId].details;
		await this.createAuditRecord({
			eventId: this.eventid,
			eventRecordTypeId: 136,
			objectId,
			details: `Linked task ${taskId} to response: ${responseDetails}`,
		});

		this.closeTaskModal();
	}

	/**
	 * Deletes a pre-existing task metadata item linked to the current response
	 * open in the task modal.
	 */
	private async deleteTaskLink(metadata: Metadata) {
		const responseMetadata = this.responsesMetadata[this.taskModalState.responseId];
		await this.deleteMetadata(metadata);

		// Create an audit record
		const taskId = metadata.data.taskId;
		const responseId = this.taskModalState.responseId;
		const responseDetails = this.activeResponses[responseId].details;
		await this.createAuditRecord({
			eventId: this.eventid,
			eventRecordTypeId: 137,
			objectId: responseId,
			details: `Removed linked task ${taskId} from response: ${responseDetails}`,
		});

		this.closeTaskModal();
	}
}
